import { createFeature, createReducer, on } from '@ngrx/store';
import { InternalListOrganization } from 'src/app/accounts/lists.component.types';
import { ICreateAccount } from 'src/app/shared/account/create-account.helper';
import {
  addToAdvertisersStats,
  addToAdvertisersStatsLoadingIds,
  addToPublishersStats,
  addToPublishersStatsLoadingIds,
  fetchAdvertisers,
  fetchPublishers,
  setAdvertisers,
  setAdvertisersFilter,
  setNextAdvertisersPage,
  setNextPublishersPage,
  setOrganizations,
  setPublishers,
  setPublishersFilter
} from './accounts.actions';
import { updateState } from './accounts.helpers';
import { Account } from 'src/app/shared/account/account.types';

export interface IAccountsState {
  // Publishers
  publishers: ICreateAccount[];
  publishersStatsLoadingIds: string[];
  publishersStats: Record<string, Account.PublisherStats>;
  isPublishersLoading: boolean;
  publisherPage: number;
  publisherFilter: string;

  // Advertisers
  advertisers: ICreateAccount[];
  advertisersStatsLoadingIds: string[];
  advertisersStats: Record<string, Account.AdvertiserStats>;
  isAdvertisersLoading: boolean;
  advertiserPage: number;
  advertiserFilter: string;

  organizations: InternalListOrganization[];
}

export const initState: IAccountsState = {
  publishers: [],
  publishersStatsLoadingIds: [],
  publishersStats: {},
  isPublishersLoading: false,
  publisherPage: 1,
  publisherFilter: '',

  advertisers: [],
  advertisersStatsLoadingIds: [],
  advertisersStats: {},
  isAdvertisersLoading: false,
  advertiserPage: 1,
  advertiserFilter: '',

  organizations: [],
};

export const ACCOUNTS_FEATURE_NAME = 'accounts';

export const accountsFeature = createFeature({
  name: ACCOUNTS_FEATURE_NAME,
  reducer: createReducer(
    initState,

    // Publishers
    on(fetchPublishers, (state): IAccountsState => updateState(state, {
      isPublishersLoading: true,
      publisherFilter: '',
      publisherPage: initState.publisherPage,
    })),

    on(setPublishers, (state, { publishers }): IAccountsState => updateState(state, {
      publishers,
      isPublishersLoading: false,
    })),

    on(addToPublishersStats, (state, { stats }): IAccountsState => {
      const publishersStats = stats.reduce((acc, stat) => {
        acc[stat.shopId] = stat.stats;
        return acc;
      }, { ...state.publishersStats });

      const publishersStatsLoadingIds = [...state.publishersStatsLoadingIds.filter(id => !publishersStats?.[id])];

      return updateState(state, {
        publishersStats,
        publishersStatsLoadingIds,
      });
    }),

    on(addToPublishersStatsLoadingIds, (state, { ids }): IAccountsState => updateState(state, {
      publishersStatsLoadingIds: [...state.publishersStatsLoadingIds, ...ids],
    })),

    on(setNextPublishersPage, (state): IAccountsState => updateState(state, {
      publisherPage: state.publisherPage + 1,
    })),

    on(setPublishersFilter, (state, { searchTerm }): IAccountsState => updateState(state, {
      publisherFilter: searchTerm,
      publisherPage: initState.publisherPage,
    })),

    // Advertisers
    on(fetchAdvertisers, (state): IAccountsState => updateState(state, {
      isAdvertisersLoading: true,
      advertiserFilter: '',
      advertiserPage: initState.advertiserPage,
    })),

    on(setAdvertisers, (state, { advertisers }): IAccountsState => updateState(state, {
      advertisers,
      isAdvertisersLoading: false,
    })),

    on(addToAdvertisersStats, (state, { stats }): IAccountsState => {
      const advertisersStats = stats.reduce((acc, stat) => {
        acc[stat.advertiserId] = stat.stats;
        return acc;
      }, { ...state.advertisersStats });
      const advertisersStatsLoadingIds = [...state.advertisersStatsLoadingIds.filter(id => !advertisersStats?.[id])];

      return updateState(state, {
        advertisersStats,
        advertisersStatsLoadingIds,
      });
    }),

    on(addToAdvertisersStatsLoadingIds, (state, { ids }): IAccountsState => updateState(state, {
      advertisersStatsLoadingIds: [...state.advertisersStatsLoadingIds, ...ids],
    })),

    on(setNextAdvertisersPage, (state): IAccountsState => updateState(state, {
      advertiserPage: state.advertiserPage + 1,
    })),

    on(setAdvertisersFilter, (state, { searchTerm }): IAccountsState => updateState(state, {
      advertiserFilter: searchTerm,
      advertiserPage: initState.advertiserPage,
    })),

    on(setOrganizations, (state, { organizations }) => updateState(state, {
      organizations,
    })),
  ),
});

export const {
  name,

  selectPublishers,
  selectIsPublishersLoading,
  selectPublisherPage,
  selectPublisherFilter,
  selectPublishersStatsLoadingIds,
  selectPublishersStats,

  selectAdvertisers,
  selectIsAdvertisersLoading,
  selectAdvertiserPage,
  selectAdvertiserFilter,
  selectAdvertisersStatsLoadingIds,
  selectAdvertisersStats,

  selectOrganizations,
} = accountsFeature;
